import { skipToken } from '@apollo/client/react'
import type { InferContractRouterOutputs } from '@orpc/contract'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import type { ValueOf } from 'type-fest'
import { UIDialog } from 'src/components/ui/Modal'
import type {
  FileUploadRecord,
  GenerationModelBaseFragment,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { setTimeoutPoll } from 'src/utils/common'
import { getNodesFromConnection } from 'src/utils/graphql'
import { isSDV1Model, isXLModel } from 'src/utils/model'
import { track } from 'src/utils/tracker'
import {
  executeDeleteGenerationModel,
  executeUpsertGenerationModel,
  fetchGetFileUploadRecord,
  fetchGetGenerationModel,
  usePagedListGenerationModels,
} from '../apollo/generation-model'
import { useGenerationModelImportExternalModelMutation } from '../orpc/generationModel'

type GenerationModelImportExternalModelResponse =
  InferContractRouterOutputs<BaseContract>['generationModel']['importExternalModel']

export function confirmModelDeletion(): Promise<boolean> {
  // TODO: i18n
  return UIDialog.confirm({
    heading: 'Delete model?',
    content: 'Are you sure you want to delete this model?',
    confirmProps: {
      children: 'Delete',
      color: 'danger',
    },
  })
}

/**
 * Delete a model
 *
 * @returns The operation result
 *   - `true`: The operation was successful
 *   - `false`: Error occurred during the operation
 *   - `undefined`: The operation was canceled
 */
export async function deleteModel(
  model:
    | undefined
    | string
    | Pick<GenerationModelBaseFragment, 'id' | 'type'>
    | Promise<Pick<GenerationModelBaseFragment, 'id' | 'type'> | undefined>,
  opts?: {
    /** Skip confirmation (like `--quiet`) */
    skipConfirm?: boolean
  },
) {
  let targetModel =
    typeof model === 'string'
      ? fetchGetGenerationModel({ id: model }).then(
          res => res?.data?.generationModel,
        )
      : model

  if (opts?.skipConfirm || (await confirmModelDeletion())) {
    targetModel = await targetModel
    if (!targetModel) return true
    track('delete_model_click', {
      modelId: targetModel.id,
      type: targetModel.type,
    })

    try {
      await executeDeleteGenerationModel({ id: targetModel.id })
      // TODO: I18N
      toast.success('Delete successfully')
      return true
    } catch (err) {
      // TODO: I18N
      toast.error(`Failed to delete. ${String(err)}`)
      return false
    }
  }
}

export async function publishModel(modelId: string) {
  const { data } = await executeUpsertGenerationModel({
    id: modelId,
    input: { isPrivate: false },
  })
  return data?.upsertGenerationModel
}

async function checkUpload(id: string): Promise<Partial<FileUploadRecord>> {
  const { data } = await fetchGetFileUploadRecord({ id })
  return data?.fileUploadRecord ?? {}
}

export const ExternalModelStatus = {
  Idle: 'idle',
  Validating: 'validating',
  Uploading: 'uploading',
  Success: 'success',
  Error: 'error',
} as const
export type ExternalModelStatusValues = ValueOf<typeof ExternalModelStatus>

export function useExternalModelHelpers() {
  const [url, setUrl] = useState('')
  const [modelInfo, setModelInfo] =
    useState<GenerationModelImportExternalModelResponse>()
  // oxfmt-ignore
  const [status, setStatus] = useState<ExternalModelStatusValues>(
    ExternalModelStatus.Idle,
  )
  const importModelMutation = useGenerationModelImportExternalModelMutation()
  const [error, setError] = useState<any>()

  let cancelPolling: (() => void) | undefined

  const { t } = useTranslation(['market'])

  // oxfmt-ignore
  async function importModel(url: string) {
    reset()
    setUrl(url)
    setStatus(ExternalModelStatus.Validating)
    try {
      const info = await importModelMutation.mutateAsync({ url })
      setModelInfo(info)
      if (info.fileId) {
        if (info.exists) {
          setStatus(ExternalModelStatus.Success)
          track('fetching_model_success', { exist: true })
        } else {
          setStatus(ExternalModelStatus.Uploading)
          const { fileId } = info
          const cancel = (cancelPolling = setTimeoutPoll(async () => {
            const { isFinished } = await checkUpload(fileId)
            if (isFinished) {
              cancel()
              setStatus(ExternalModelStatus.Success)
              track('fetching_model_success', { exist: false })
            }
          }, 10_000 /* 10s */))
        }
      } else {
        setStatus(ExternalModelStatus.Idle)
      }
      return info
    } catch (e: any) {
      console.error(e)
      setStatus(ExternalModelStatus.Error)
      setError(e)
      toast.error(
        t('market:importer.messages.importing-failed', { error: e instanceof Error ? e.message : e.toString() }),
      )
      return Promise.reject(e)
    }
  }

  function reset() {
    setUrl('')
    setStatus(ExternalModelStatus.Idle)
    setError(undefined)
    cancelPolling?.()
    cancelPolling = undefined
  }

  return {
    importModel,
    url,
    modelInfo,
    status,
    error,
    validating: status === ExternalModelStatus.Validating,
    uploading: status === ExternalModelStatus.Uploading,
    success: status === ExternalModelStatus.Success,
    reset,
  }
}

/*
 * ==== VAE Models =============================================================
 */

export const PRESET_AUTHOR_ID = '1544489361907720074'

export function useVaeModels(modelType?: GenerationModelType) {
  const type = isSDV1Model(modelType)
    ? GenerationModelType.Vae
    : isXLModel(modelType)
      ? GenerationModelType.SdxlVae
      : undefined
  return usePagedListGenerationModels(
    type
      ? {
          first: 30,
          type,
          authorId: PRESET_AUTHOR_ID,
        }
      : skipToken,
  )
}

export function usePresetModels() {
  const res = usePagedListGenerationModels(
    {
      feed: 'preset',
      type: GenerationModelType.AnyModel,
    },
    {
      context: {
        refetchOnFocus: false,
      },
    },
  )
  const modelList = getNodesFromConnection(res.data)

  /**
   * initialLoading: to distinguish between first time loading and subsequent loading
   * For performance optimization
   * Should be unnecessary once we upgraded to apollo client v4
   */
  return { modelList, initialLoading: !res.data && res.loading, ...res }
}
